import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBranding, logoLogin } from '../context/BrandingContext';

// Login com Google só aparece quando a imagem foi gerada com o client_id;
// sem ele o botão aparecia e o Google recusava (QA 2026-09).
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const googleButtonRef = useRef(null);

  const { login, loginWithGoogle } = useAuth();
  const { branding, pronto } = useBranding();
  const navigate = useNavigate();

  const handleGoogleSuccess = useCallback(async (credentialResponse) => {
    setError('');
    setLoading(true);
    const result = await loginWithGoogle(credentialResponse.credential);

    if (result.success) {
      toast.success('Login com Google realizado!');
      navigate('/busca');
    } else {
      setError(result.error);
      toast.error(result.error);
    }
    setLoading(false);
  }, [loginWithGoogle, navigate]);

  // Aguardar o script do Google carregar
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // sem client_id o Google recusa (400): nem tenta
    let attempts = 0;
    const maxAttempts = 20; // 10 segundos max

    const checkGoogle = () => {
      if (window.google?.accounts?.id) {
        setGoogleReady(true);
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkGoogle, 500);
      } else {
        console.error('Google Sign-In não carregou após 10s');
      }
    };

    checkGoogle();
  }, []);

  // Renderizar botão quando Google estiver pronto
  useEffect(() => {
    if (!googleReady || !googleButtonRef.current) return;

    // Evitar inicialização múltipla
    if (googleButtonRef.current.children.length > 0) return;

    try {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleSuccess,
      });

      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 300,
        text: 'signin_with',
      });
    } catch (err) {
      console.error('Erro ao renderizar botão Google:', err);
    }
  }, [googleReady, handleGoogleSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/busca');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="login-container">
      <div className="login-box">
        {pronto
          ? <img src={logoLogin(branding)} alt={branding.nome} style={{ height: '80px', marginBottom: '1rem', display: 'block', marginLeft: 'auto', marginRight: 'auto' }} />
          : <div aria-hidden="true" style={{ height: '80px', marginBottom: '1rem' }} />}
        <p style={{ textAlign: 'center', color: 'var(--gray-600)', marginBottom: '1.5rem' }}>Sistema de Atendimento</p>

        {error && <div className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                required
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-500)',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? (
              'Entrando...'
            ) : (
              <>
                <LogIn size={18} />
                Entrar
              </>
            )}
          </button>

          <div style={{ textAlign: 'center', marginTop: '0.85rem' }}>
            <button
              type="button"
              onClick={() => navigate('/recuperar-senha')}
              style={{ background: 'none', border: 'none', color: 'var(--brand, #20a490)', cursor: 'pointer', fontSize: '0.88rem', textDecoration: 'underline', padding: 0 }}
            >
              Esqueci minha senha
            </button>
          </div>

          {GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: '1.5rem', borderTop: '1px solid var(--gray-300)', paddingTop: '1.5rem' }}>
              <p style={{ textAlign: 'center', color: 'var(--gray-600)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Ou entre com
              </p>
              <div ref={googleButtonRef} style={{ display: 'flex', justifyContent: 'center' }} />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
